import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild
} from '@angular/core';

@Component({
  selector: 'app-why-hungery-external',
  templateUrl: './why-hungery-external.component.html',
  styleUrls: ['./why-hungery-external.component.scss']
})
export class WhyHungeryExternalComponent implements AfterViewInit, OnDestroy {
  @ViewChild('container', { read: ElementRef }) container!: ElementRef;

  private guestEl?: HTMLElement;
  private guestLoaded = false;
  private guestPresentationTimer?: number;
  private guestPresentationAttempts = 0;

  ngAfterViewInit() {
    this.loadGuestScripts(() => {
      this.waitForJQuery(() => {
        this.guestLoaded = true;
        this.mountGuest();
      });
    });
  }

  ngOnDestroy() {
    if (this.guestPresentationTimer) {
      window.clearTimeout(this.guestPresentationTimer);
      this.guestPresentationTimer = undefined;
    }
    this.unmountGuest();
  }

  private loadGuestScripts(onReady: () => void) {
    const scripts = [
      'assets/why-hungery/scripts/why-hungery-polyfills.js',
      'assets/why-hungery/scripts/why-hungery-main.js'
    ];

    let loaded = 0;
    const ensure = () => {
      loaded += 1;
      if (loaded === scripts.length) {
        onReady();
      }
    };

    scripts.forEach(src => {
      const old = document.querySelector(`script[src="${src}"]`);
      if (old) {
        old.remove();
      }

      const s = document.createElement('script');
      s.src = src;
      s.async = false;
      s.onload = ensure;
      s.onerror = (e) => console.error('Error loading guest script:', src, e);
      document.body.appendChild(s);
    });
  }

  private waitForJQuery(onReady: () => void) {
    const check = () => {
      if ((window as any).$ && typeof (window as any).$ === 'function') {
        onReady();
      } else {
        setTimeout(check, 50);
      }
    };
    check();
  }

  private mountGuest() {
    if (!this.container?.nativeElement || !this.guestLoaded) return;

    this.container.nativeElement.innerHTML = '';

    const el = document.createElement('guest-app-why-hungery') as HTMLElement & {
      title?: string;
      message?: string;
    };

    el.title = 'Dashboard Widget';
    el.message = 'Hello from Host!';

    el.addEventListener('eventFromGuest', (ev: Event) => {
      const { detail } = ev as CustomEvent<string>;
      console.log('Host received event:', detail);
    });

    this.container.nativeElement.appendChild(el);
    this.guestEl = el;
    this.applyGuestPresentation();
  }

  private unmountGuest() {
    if (this.guestEl && this.guestEl.parentElement) {
      this.guestEl.parentElement.removeChild(this.guestEl);
      this.guestEl = undefined;
    }
    if (this.container?.nativeElement) {
      this.container.nativeElement.innerHTML = '';
    }
    this.guestLoaded = false;
  }

  /**
   * The temporary Why Hungery page is delivered as a pre-built web component.
   * Its sections use open shadow roots, so host styles cannot reach the two
   * presentation defects directly. Keep these narrow overrides here until the
   * guest application's original source replaces the bundled component.
   */
  private applyGuestPresentation() {
    const guestRoot = this.guestEl?.shadowRoot;
    const solutionSection = guestRoot
      ? this.findInOpenShadowRoots('app-solution', guestRoot)
      : undefined;
    const joinStepsSection = guestRoot
      ? this.findInOpenShadowRoots('app-join-steps', guestRoot)
      : undefined;

    if (solutionSection?.shadowRoot) {
      this.appendShadowStyle(
        solutionSection.shadowRoot,
        'why-hungery-solution-fixes',
        `
          .benefit-icon .material-icons {
            display: inline-block !important;
            width: 48px;
            height: 48px;
            font-size: 0 !important;
            background-position: center;
            background-repeat: no-repeat;
            background-size: contain;
          }

          .benefit-card:nth-child(1) .benefit-icon .material-icons {
            background-image: url('/assets/why-hungery/icons/local-discovery.svg');
          }

          .benefit-card:nth-child(2) .benefit-icon .material-icons {
            background-image: url('/assets/why-hungery/icons/free-registration.svg');
          }

          .benefit-card:nth-child(3) .benefit-icon .material-icons {
            background-image: url('/assets/why-hungery/icons/direct-visits.svg');
          }

          .play-pause-btn-center .material-icons,
          .play-pause-btn-controls .material-icons {
            display: inline-block;
            position: relative;
            width: 28px;
            height: 28px;
            font-size: 0 !important;
          }

          .play-pause-btn-center .material-icons::before,
          .play-pause-btn-controls[aria-label='Play'] .material-icons::before {
            content: '';
            position: absolute;
            top: 50%;
            left: 54%;
            width: 0;
            height: 0;
            border-top: 10px solid transparent;
            border-bottom: 10px solid transparent;
            border-left: 16px solid #fff;
            transform: translate(-50%, -50%);
          }

          .play-pause-btn-controls[aria-label='Pause'] .material-icons::before,
          .play-pause-btn-controls[aria-label='Pause'] .material-icons::after {
            content: '';
            position: absolute;
            top: 4px;
            bottom: 4px;
            width: 6px;
            border-radius: 1px;
            background: #fff;
          }

          .play-pause-btn-controls[aria-label='Pause'] .material-icons::before {
            left: 5px;
          }

          .play-pause-btn-controls[aria-label='Pause'] .material-icons::after {
            right: 5px;
          }
        `
      );
    }

    if (joinStepsSection?.shadowRoot) {
      this.appendShadowStyle(
        joinStepsSection.shadowRoot,
        'why-hungery-join-steps-fixes',
        `
          .join-steps-section {
            min-height: 0;
            padding: 64px 32px;
            background-position: center;
          }

          .content-grid {
            grid-template-columns: minmax(280px, 340px) minmax(0, 1fr);
            gap: clamp(48px, 6vw, 88px);
          }

          .steps-column {
            gap: 16px;
            max-width: 340px;
          }

          .step-card {
            padding: 28px 24px;
            border-radius: 16px;
          }

          .step-title {
            font-size: 1.25rem;
            margin-bottom: 16px;
          }

          .step-button,
          .step-arrows {
            margin-bottom: 16px;
          }

          .ui-image-container {
            height: clamp(240px, 28vw, 360px);
            margin-bottom: 32px;
          }

          @media (max-width: 992px) {
            .join-steps-section {
              padding: 48px 24px;
            }

            .content-grid {
              grid-template-columns: 1fr;
              gap: 48px;
            }

            .steps-column {
              max-width: 560px;
            }
          }

          @media (max-width: 576px) {
            .join-steps-section {
              padding: 40px 16px;
            }

            .step-card {
              padding: 24px 20px;
            }

            .ui-image-container {
              height: 240px;
              margin-bottom: 24px;
            }
          }
        `
      );
    }

    if (solutionSection?.shadowRoot && joinStepsSection?.shadowRoot) {
      this.guestPresentationTimer = undefined;
      return;
    }

    if (this.guestPresentationAttempts < 40) {
      this.guestPresentationAttempts += 1;
      this.guestPresentationTimer = window.setTimeout(
        () => this.applyGuestPresentation(),
        50
      );
    }
  }

  private findInOpenShadowRoots(
    selector: string,
    root: ShadowRoot
  ): HTMLElement | undefined {
    const match = root.querySelector<HTMLElement>(selector);
    if (match) return match;

    const elements = root.querySelectorAll<HTMLElement>('*');
    for (const element of Array.from(elements)) {
      if (!element.shadowRoot) continue;
      const nestedMatch = this.findInOpenShadowRoots(selector, element.shadowRoot);
      if (nestedMatch) return nestedMatch;
    }

    return undefined;
  }

  private appendShadowStyle(
    root: ShadowRoot,
    id: string,
    css: string
  ) {
    if (root.getElementById(id)) return;

    const style = document.createElement('style');
    style.id = id;
    style.textContent = css;
    root.appendChild(style);
  }
}
